import { Dynamo } from '..';
import {
    Coding,
    EditablePrimaryRecordResource,
    EditablePrimaryRecordResourceWithoutId,
    PrimaryRecordAllergy,
    PrimaryRecordCondition,
    PrimaryRecordLab,
    PrimaryRecordMedication,
    PrimaryRecordResource,
    PrimaryRecordResourceCategory,
    PrimaryRecordVisit,
    ResourceMetadata,
} from '../MedicalDataTypes';
import { AvatarChange, EntityReferences } from './sharedTypes';

export type TotalCounts = Record<PrimaryRecordResourceCategory, number>;

export type FilteredCounts = {
    activeMedicationCount: number;
    activeConditionCount: number;
    upcomingVisitCount: number;
    activeAllergyCount: number;
    imagingCount: number;
    vaccinationCount: number;
    procedureCount: number;
    labCount: number;
};

export type ResourceCountsResponse = {
    totals: TotalCounts;
    filtered: FilteredCounts;
};

export type ResourceWithDateAdded<TResourceType extends PrimaryRecordResource = any> = TResourceType & {
    dateAdded: string;
};

export type GroupingMetadata = {
    groupId: string;
    isPrimary: boolean;
};

export type ListResourceResponseItem<TResourceType extends PrimaryRecordResource = any> =
    ResourceWithDateAdded<TResourceType> & {
        metadata: Omit<ResourceMetadata, 'references'>;
    } & {
        groupingMetadata: GroupingMetadata;
        parentCoding?: Coding;
    };

export type GetResourceReferencesRequest = {
    resourceIds: string[];
};

export type GetResourceReferencesListItem = {
    resourceId: string;
    references: EntityReferences;
};

export type GetResourceReferencesResponse = {
    references: GetResourceReferencesListItem[];
};

export type ListResourcesResponse<TResourceType extends PrimaryRecordResource = any> = {
    resources: ListResourceResponseItem<TResourceType>[];
};

export type LabDateBucket = {
    date: string;
    labCount: number;
};

export type GetUniqueLabCodesResponse = {
    codes: {
        coding: Coding;
        labCount: number;
        mostRecentDateTime: string;
    }[];
};

/**
 * Return buckets/groups of labs that occurred on the same day
 */
export type GetLabDateBucketResponse = {
    labDateBuckets: LabDateBucket[];
};

export type GetPanelsForDateRequest = {
    date: string;
};

export type Panel = {
    panelName: string;
    labs: PrimaryRecordLab[];
};

/**
 * Return panels for a given date.  The are grouped by best effort to present to the user in a logical group and within-group order
 */
export type GetPanelsForDateResponse = {
    panels: Panel[];
    unmatchedLabs: PrimaryRecordLab[];
};

export type WithHealthSystemName<TBase> = {
    healthSystemName: string | null;
} & TBase;

export type PrimaryRecordLabWithHealthSystemName = WithHealthSystemName<PrimaryRecordLab>;

/**
 * Given an analyte, return the labs that have that analyte.  This is used to show a measurement history to the user
 */
export type GetLabHistoryResponse = {
    labHistory: PrimaryRecordLabWithHealthSystemName[];
};

export type GetLabHistoryRequest = {
    code: string;
};

export type UpdateResourceBody<TResourceType extends EditablePrimaryRecordResource = EditablePrimaryRecordResource> = {
    resourceUpdate: Partial<TResourceType>;
    imageUpdates: ResourceImageUpdate[];
    /**
     * Carries the category's status field across to the other resources de-duplicated into the
     * same group. The group is resolved on the server, so this cannot reach a resource that is
     * not actually grouped with the one being edited. Only the status propagates - every other
     * field stays on the edited resource, since each entry keeps its own details from the
     * system it came from.
     */
    propagateStatusToGroup?: boolean;
};

export type ResourceImageUpdateExisting = {
    type: 'update';
    resourceImageId: string;
    description: string;
    imageChange: AvatarChange;
};

export type ResourceImageUpdate = ResourceImageUpdateExisting | ResourceImageCreate;

export type ResourceImage = {
    description: string;
    resourceImageId: string;
};

export type ResourceImageCreate = {
    type: 'create';
    tempFileId: string;
    description: string;
};

export type CreateResourceRequest<
    TResourceType extends EditablePrimaryRecordResourceWithoutId = EditablePrimaryRecordResourceWithoutId,
> = {
    resource: TResourceType;
    images: ResourceImageCreate[];
    sourceResourceId: string | null;
    careTeamMemberId?: string;
    /** Visits only: org note to copy onto the created visit as a 'visit-resource' note. */
    sourceNoteId?: string;
};

export type CreateResourceResponse = {
    resourceId: string;
};

export type BulkCreateResourcesRequest<
    TResourceType extends EditablePrimaryRecordResourceWithoutId = EditablePrimaryRecordResourceWithoutId,
> = {
    // sourceNoteId is single-create only (the org bulk-share path handles its own note copy);
    // this handler does not copy notes, so the record type/schema rejects the field loudly
    // (400) rather than silently ignoring it.
    records: (Omit<CreateResourceRequest<TResourceType>, 'sourceNoteId'> & { sourceNoteId?: undefined })[];
};

export type ResourceDetailsResponse<TResourceType extends PrimaryRecordResource = any> = {
    resource: ListResourceResponseItem<TResourceType>;
    metadata: ResourceMetadata;
    updatedBy: string | null;
    updatedDateTime: string | null;
    images: ResourceImage[];
    createdByUser: Dynamo.User | null;
    groupedResources: ListResourceResponseItem<TResourceType>[];
    parentCoding?: Coding;
};

export type ResourceGroupRelatedResourcesRequest = {
    groupResourceIds: string[];
};

export type ResourceGroupRelatedResourcesResponse = ListRelatedResourcesResponse;

export type ListRelatedResourcesResponse = {
    resources: ListResourceResponseItem<PrimaryRecordResource>[];
    category: PrimaryRecordResourceCategory;
};

export type UpdateRelatedResourceRequest = {
    relatedResourceIds: string[];
    groupResourceIds: string[];
};

export type UpdateResourceCareTeamRequest = {
    careTeamMemberIds: string[];
    resourceGroupIds: string[];
};

export type UpdateCareTeamResourcesRequest = {
    resourceIds: string[];
};

export type BulkUpdateConditions = {
    resourceCategory: 'condition';
    status: PrimaryRecordCondition['status'];
};

export type BulkUpdateAllergies = {
    resourceCategory: 'allergy';
    status: PrimaryRecordAllergy['status'];
};

export type BulkUpdateMedications = {
    resourceCategory: 'medication';
    isActive: PrimaryRecordMedication['isActive'];
};

type WithBulkUpdateResourcesBaseProps<T> = {
    action: 'update';
    resourceCategory: PrimaryRecordResourceCategory;
    resourceIds: string[];
} & T;

export type BulkUpdateConditionsRequest = WithBulkUpdateResourcesBaseProps<BulkUpdateConditions>;
export type BulkUpdateAllergiesRequest = WithBulkUpdateResourcesBaseProps<BulkUpdateAllergies>;
export type BulkUpdateMedicationsRequest = WithBulkUpdateResourcesBaseProps<BulkUpdateMedications>;

export type BulkUpdateResources =
    | BulkUpdateConditionsRequest
    | BulkUpdateAllergiesRequest
    | BulkUpdateMedicationsRequest;

export type BulkDeleteResources = {
    action: 'delete';
    resourceCategory: PrimaryRecordResourceCategory;
    resourceIds: string[];
};

export type BulkEditRequest = BulkDeleteResources | BulkUpdateResources;

/** "This and following" applies from the edited occurrence onward; "all" covers the whole series. */
export type UpdateVisitSeriesScope = 'all' | 'following';

export type UpdateVisitSeriesRequest = {
    recurrenceGroupId: string;
    scope: UpdateVisitSeriesScope;
    /** Start of the edited occurrence — the boundary for the "following" scope. */
    fromDateTime: string;
    /**
     * The edited visit fields. Applied to every in-scope occurrence, except each keeps its
     * own date: only the time-of-day and duration from this update's start/end propagate.
     */
    resourceUpdate: Partial<PrimaryRecordVisit>;
};

export type GetRelatedResourceSuggestionsResponse = {
    suggestions: {
        resourceId: string;
        type: PrimaryRecordResourceCategory;
        explanation: string;
    }[];
};
export type GetRelatedResourceDiscoveriesResponse = {
    discoveries: {
        name: string;
        type: PrimaryRecordResourceCategory;
        explanation: string;
    }[];
};

export type SearchVaccinationTypesRequest = {
    searchString: string;
};

export type SearchVaccinationTypesResponse = {
    exactMatch?: Coding;
    substringSearchResults: Coding[];
    fuzzySearchResults: Coding[];
};

export type GetResourceDescriptionResponse = {
    description: string;
};

export type FDADrug = {
    brandName: string;
    activeIngredients: {
        name: string;
        strength: string;
    }[];
    dosageForm: string;
    route: string;
};

export type FDADrugsPartialSearchRequest = {
    type: 'partial';
    searchString: string;
};

export type FDADrugsExactSearchRequest = {
    type: 'exact';
    brandName: string;
    activeIngredients: string[];
    dosageForm?: string;
    route?: string;
};

export type SearchFDADrugsRequest = FDADrugsPartialSearchRequest | FDADrugsExactSearchRequest;

export type SearchFDADrugsResponse = {
    items: FDADrug[];
};

export type MedicalProfileVisitInviteEmailItem = {
    inviteeEmail: string;
};

export type ListMedicalProfileVisitInviteEmailsResponse = {
    medicalProfileVisitInviteEmails: MedicalProfileVisitInviteEmailItem[];
};

export type UpdateMedicalProfileVisitInviteEmailsRequest = {
    emails: string[];
};
